import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../../lib/api.js';

// Per-article feedback state. Loads the editor's rows for a day, exposes a map
// keyed by articleId, and offers an optimistic upsert that posts a partial row
// to /api/feedback/articles. The server upsert is keyed on (editorId, articleId)
// so retries don't fork the state machine.
export function useArticleFeedback(isoDate) {
  const [byArticleId, setByArticleId] = useState({}); // articleId -> feedback row
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!isoDate) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/feedback/articles/${isoDate}`);
      const next = {};
      for (const row of data.feedback || []) {
        next[String(row.articleId)] = row;
      }
      setByArticleId(next);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isoDate]);

  useEffect(() => {
    load();
  }, [load]);

  const update = useCallback(
    async (articleId, patch) => {
      if (!articleId) {
        // Without a real article _id every feedback row would collide on the
        // same map key — surface the misconfig loudly instead of silently
        // overwriting one article's row with another's answer.
        const err = new Error('Cannot save feedback: article is missing _id');
        setError(err.message);
        if (typeof console !== 'undefined') console.error(err);
        return;
      }
      const id = String(articleId);
      // Optimistic local merge.
      setByArticleId((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || { articleId: id, isoDate }), ...patch },
      }));
      try {
        const data = await api.post('/feedback/articles', {
          isoDate,
          articleId,
          ...patch,
        });
        setByArticleId((prev) => ({ ...prev, [id]: data.feedback }));
      } catch (err) {
        setError(err.message);
      }
    },
    [isoDate]
  );

  const ratedCount = Object.values(byArticleId).filter((f) => !!f?.q1Answer).length;

  return { byArticleId, loading, error, update, ratedCount, reload: load };
}
